import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { HashRouter, Routes, Route, Navigate, useParams, useLocation } from "react-router-dom";
import Index from "./pages/Index";
import { I18nextProvider, useTranslation } from "react-i18next";
import i18n from "@/lib/i18n";
import React from "react";
import { withPersistentParams } from "@/utils/urlParams";

const queryClient = new QueryClient();

const LegacyReferralRedirect: React.FC<{ getLocalizedRoute: (pathKey: string) => string }> = ({ getLocalizedRoute }) => {
  const location = useLocation();
  const confirmationPath = getLocalizedRoute('routes.confirmation');
  return <Navigate to={`${confirmationPath}${location.search}`} replace />;
};

// This component will contain the actual application routes,
// assuming the locale has already been established in the URL.
const LocalizedRoutes: React.FC = () => {
  const { locale } = useParams<{ locale: string }>();
  const { i18n, t } = useTranslation();

  // Ensure i18n language is set based on URL locale
  React.useEffect(() => {
    if (locale && i18n.language !== locale) {
      i18n.changeLanguage(locale);
    }
  }, [locale, i18n]);

  // Helper to get localized path for routes.
  // Uses the URL locale directly (via i18n.getResource) instead of t(), so that routes are
  // always resolved from the URL locale even when i18n hasn't caught up yet on the first render
  // (e.g. when window.__simpego_widget__.locale differs from the hash locale).
  const getLocalizedRoute = (pathKey: string) => {
    const baseRoute = (locale ? i18n.getResource(locale, 'translation', pathKey) : t(pathKey)) as string;
    return baseRoute?.startsWith('/') ? baseRoute : `/${baseRoute ?? ''}`;
  };

  return (
    <Routes>
      <Route path={getLocalizedRoute('routes.home')} element={<Index />} />
      <Route path={getLocalizedRoute('routes.petDetails')} element={<Index />} />
      <Route path={getLocalizedRoute('routes.productVariantSelection')} element={<Index />} />
      <Route path={getLocalizedRoute('routes.productCustomization')} element={<Index />} />
      <Route path={getLocalizedRoute('routes.addressDetails')} element={<Index />} />
      <Route path={getLocalizedRoute('routes.confirmation')} element={<Index />} />
      <Route path="/referral" element={<LegacyReferralRedirect getLocalizedRoute={getLocalizedRoute} />} />
      <Route path="/closing" element={<LegacyReferralRedirect getLocalizedRoute={getLocalizedRoute} />} />

      {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
      <Route path="*" element={<Navigate to={withPersistentParams(getLocalizedRoute('routes.home'))} replace />} />
    </Routes>
  );
};

// This component handles the initial locale detection and redirection
const LocaleHandler: React.FC = () => {
  const { i18n } = useTranslation();
  const location = useLocation();

  const hashPath = location.pathname;
  const pathSegments = hashPath.split('/').filter(Boolean);

  const isLocaleInPath = pathSegments.length > 0 && i18n.options.supportedLngs?.includes(pathSegments[0]);

  if (!isLocaleInPath) {
    const defaultLocale = i18n.language || i18n.options.fallbackLng?.[0] || 'en';
    const targetHash = `/${defaultLocale}${hashPath}${location.search}`;
    return <Navigate to={targetHash} replace />;
  }

  return (
    <Routes>
      <Route path="/:locale/*" element={<LocalizedRoutes />} />
      <Route path="*" element={<Navigate to={withPersistentParams(`/${i18n.language || i18n.options.fallbackLng?.[0] || 'en'}`)} replace />} />
    </Routes>
  );
};

const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider delayDuration={100}>
        <HashRouter>
          <LocaleHandler />
        </HashRouter>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;
